import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AVAILABLE_STOCK, IS_PREBOOK_FLOW } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import useIsMobile from '@/hooks/useIsMobile';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { Product, ProductVariant } from '@/models/Product';
import scrollStyles from '@/styles/HideScrollbar.module.css';
import styles from '@/styles/TruncateContent.module.css';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { captureViewEvent
// trackCustomEvent,
} from '@/utils/analytics/googleAnalytics';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import { formatProductData } from '@/utils/cart';
import { getLocalStorageKey, isArray } from '@/utils/helper';
import BottomDrawer from '../BottomDrawer';
import EditIcon2 from '../icons/EditIcon2';
import Star from '../icons/growth/Star';
import Paragraph, { VARIANT as PARAGRAPH_VARIANT } from '../Paragraph';
import Wishlist from '../Wishlist';
import HyperLocalAddToCart from './HyperLocalAddToCart';
import InsuranceLabel from './InsuranceLabel';
import ModifyPriceDrawer from './ModifyPriceDrawer';
import ProductListingImage from './ProductListingImage';
import ProductPrice from './ProductPrice';
import ProductVariantList from './ProductVariantList';
import VariantLabel from './VariantLabel';

// import OutOfStockProductCard from '../cart/OutOfStockProductCard'
// import ProductImage from './ProductImage'
// import VariantAddedMessage from './VariantAddedMessage'

const getActiveVariants = (variants: ProductVariant[], listType?: ProductListTypeEnum) => {
  return variants.filter(variant => {
    if (listType === ProductListTypeEnum.SEARCH) {
      return isArray(variant.inventory_set);
    }
    return variant.active && isArray(variant.inventory_set);
  });
};
interface Props {
  hideOutOfStockLabel?: boolean;
  listType?: ProductListTypeEnum;
  position?: number;
  product: Product;
  showSeeDetailsCTA?: boolean;
  showEditPrice?: boolean;
  dcId?: number;
  componentName?: string;
  componentId?: string | number;
  isWishlist?: boolean;
  onWishlistChange?: () => void;
  onShowSimilar?: () => void;
  classNames?: string;
}
const ProductListingCard = ({
  hideOutOfStockLabel = false,
  listType,
  position,
  product,
  // showSeeDetailsCTA = false,
  showEditPrice = false,
  dcId,
  componentName = '',
  componentId = '',
  isWishlist,
  onWishlistChange,
  onShowSimilar,
  classNames
}: Props) => {
  const {
    image_url,
    name,
    brand
  } = product;
  const {
    currentVendor,
    cartItems
  } = useContext(CartContext);
  const [activeVariants, setActiveVariants] = useState<ProductVariant[]>([]);
  const [showVariantDrawer, setShowVariantDrawer] = useState(false);
  const [showModifyPriceDrawer, setShowModifyPriceDrawer] = useState(false);
  const [isUnavailableAtVendor, setIsUnavailableAtVendor] = useState(false);
  const {
    isMobile
  } = useIsMobile();
  const {
    t
  } = useTranslation('products');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const bottomDrawerRef = useRef<HTMLDivElement>(null);
  const StockInSS = useMemo(() => sessionStorage?.getItem(AVAILABLE_STOCK),
  // We need updated stock details everything quantity changes for any product
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [cartItems]);
  const availableStock = StockInSS ? JSON.parse(StockInSS) : undefined;
  const stopPropagation = useCallback((e: MouseEvent<HTMLElement>) => {
    if (!isOutOfStock && activeVariants.length > 1) {
      e.stopPropagation();
      e.preventDefault();
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [activeVariants.length]);
  useEffect(() => {
    const stock = sessionStorage?.getItem(AVAILABLE_STOCK);
    const parsedStock = stock ? JSON.parse(stock) : undefined;
    if (isArray(product.product_variants)) {
      const formatedProduct = formatProductData(product);
      const variants = getActiveVariants(formatedProduct.product_variants, listType);
      setActiveVariants(variants);
      if (parsedStock && variants[0] && parsedStock[variants[0].id] === 0) {
        setIsUnavailableAtVendor(true);
      } else {
        setIsUnavailableAtVendor(false);
      }
    }
  }, [product, cartItems, listType]);
  const toggleBottomDrawer = useCallback(() => {
    if (activeVariants.length > 1) {
      setShowVariantDrawer(prev => !prev);
    }
  }, [activeVariants.length]);
  const eventData = useMemo(() => ({
    productName: product?.name,
    productBrand: product?.brand,
    productCategory: product?.category?.name,
    fulfilmentType: currentVendor?.fulfillment_type,
    dcName: currentVendor?.fulfillment_type,
    dcMobile: currentVendor?.company_phone
  }), [product?.name, product?.brand, product?.category?.name, currentVendor?.fulfillment_type, currentVendor?.fulfillment_type, currentVendor?.company_phone]);
  const onEditClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    trackCustomEvent(CustomEventTypes.PRODUCT_EDIT_PRICE_CLICKED, JSON.stringify({
      ...eventData,
      productVariant: activeVariants[0].attribute_value[0].name,
      currentPrice: activeVariants[0].inventory_set[0].price
    }));
    setShowModifyPriceDrawer(true);
  }, [trackCustomEvent, eventData]);
  const handleModifyPrice = useCallback((selectedVariant: ProductVariant, amount: string) => {
    trackCustomEvent(CustomEventTypes.HYP_PRICED_EDITED, JSON.stringify({
      ...eventData,
      oldPrice: selectedVariant.inventory_set[0].price,
      newPrice: amount
    }));
    const changedVariantIndex = activeVariants.findIndex(item => item.id === selectedVariant.id);
    setActiveVariants(prev => {
      prev[changedVariantIndex].inventory_set[0].price = amount;
      return prev;
    });
    setShowModifyPriceDrawer(false);
  }, [trackCustomEvent, eventData]);
  const isOutOfStock = activeVariants.length === 0 || isUnavailableAtVendor;

  // const seeDetailsCTA =
  //   isOutOfStock && showSeeDetailsCTA ? (
  //     <div className="bg-primary-100 mt-4 px-[19px] py-[11px] rounded-lg w-fit text-white">
  //       {t('see_details')}
  //     </div>
  //   ) : null

  useEffect(() => {
    const preventDefault = (e: globalThis.MouseEvent) => e.preventDefault();
    const {
      current
    } = bottomDrawerRef;
    if (current) {
      current.addEventListener('click', preventDefault);
    }
    return () => {
      if (current) {
        current.removeEventListener('click', preventDefault);
      }
    };
  }, []);
  const productSlugEnc = encodeURIComponent(product?.slug?.slug);
  const {
    tag: productTag,
    product_intro: productIntro,
    review_ratings
  } = product?.product_meta || {};
  const totalRatings = review_ratings?.total_ratings || '';
  const overallRating = review_ratings?.overall_rating || '';
  const couponCode = product?.coupon_data?.couponCode || '';
  const couponDes = product?.coupon_data?.description;
  const variantsInCart = useMemo(() => activeVariants.filter(variant => Boolean(cartItems[variant.id])), [activeVariants, cartItems]);
  const isMultipleVariantsInCart = variantsInCart.length >= 2;
  const displayVariant = useMemo(() => {
    if (variantsInCart.length > 0) {
      return variantsInCart[0];
    }
    return activeVariants[0];
  }, [activeVariants, variantsInCart]);
  const quantity = useMemo(() => {
    if (isMultipleVariantsInCart) {
      return variantsInCart.reduce((acc, variant) => acc + (cartItems[variant.id] || 0), 0);
    }
    if (variantsInCart.length === 1) {
      return cartItems[variantsInCart[0].id] || 0;
    }
    return cartItems[activeVariants[0]?.id] || 0;
  }, [activeVariants, cartItems, isMultipleVariantsInCart, variantsInCart]);
  const isPrebook = !!getLocalStorageKey(IS_PREBOOK_FLOW);
  return <>
      <Link href={`/product/${productSlugEnc}${isPrebook ? '?isprebook=true' : ''}`} className={`block max-w-[240px] ${classNames}`} onClick={() => {
      trackCustomEvent(CustomEventTypes.USER_CLICKED_PRODUCT_LISTING_CARD, JSON.stringify({
        ComponentName: componentName,
        ComponentId: componentId,
        Productid: product?.id,
        ProductName: product?.name,
        vendorName: currentVendor?.company_name,
        vendorId: currentVendor?.id
      }));
      if (!isOutOfStock) {
        captureViewEvent({
          type: listType,
          product
        });
        return true;
      }
    }} data-testid={`product-link-${product.id}`} key={product.id} data-sentry-element="Link" data-sentry-source-file="ProductListingCard.tsx">
        <section className={`pb-2 flex flex-col h-[22.5rem] lg:h-[23rem] bg-white px-2 rounded-xl overflow-hidden relative flex-1 hover:shadow-xl transition-shadow duration-300  shadow-[0_4px_14px_-6px_rgba(24,39,75,0.12),_0_0_32px_-8px_rgba(24,39,75,0.10)] ease-in-out${isMobile ? 'w-full' : 'w-full'}`}>
          <div className="relative flex flex-col items-center">
            {productTag ? <p className="-left-2 z-[1] absolute bg-[#653BA3] p-2 rounded-tl-xl rounded-br-xl w-min min-w-12 font-semibold text-white text-xs text-center">
                {productTag}
              </p> : null}
            <div className="relative flex justify-center w-full text-center">
              <ProductListingImage imageUrl={image_url} productName={name} isOutOfStock={!hideOutOfStockLabel && isOutOfStock} imageClassName={'w-32 h-36'} outOfStockImageClassName={'w-32 h-36'} fallbackClassName="w-32 h-36" position={position} data-sentry-element="ProductListingImage" data-sentry-source-file="ProductListingCard.tsx" />
              <div className={isMobile ? 'right-9 bottom-2 absolute' : 'right-9 bottom-1 absolute'}>
                <Wishlist width={isMobile ? 32 : 40} height={isMobile ? 32 : 40} productInWishlist={product.in_wishlist || false} product={product} onWishlistChange={onWishlistChange} data-sentry-element="Wishlist" data-sentry-source-file="ProductListingCard.tsx" />
              </div>
              {couponCode && <div className={`flex bg-[#FFBB00] p-0.5 rounded absolute z-[1]`} style={{
              transform: 'rotate(-90deg)',
              transformOrigin: 'right center',
              top: '-10px',
              ...(isMobile ? {
                right: '15px'
              } : {
                right: '20px'
              })
            }}>
                  <div className={`p-0.5 border-dashed rounded border-white border items-center px-1 text-[10px]`}>
                    <p className="text-black leading-tight whitespace-nowrap">
                      {couponDes}
                    </p>
                    <div className="flex flex-row">
                      <p className="font-extrabold text-black leading-tight">
                        USE CODE&nbsp;
                      </p>
                      <p className="font-extrabold text-red-900 leading-tight">
                        {couponCode}
                      </p>
                    </div>
                  </div>
                </div>}
            </div>

            {product.insuranceData?.hasInsurance ? <InsuranceLabel className="z-10 -mt-6" /> : null}
          </div>
          <>
            <div className="h-[32px] lg:h-[40px]">
              <Paragraph variant={isMobile ? PARAGRAPH_VARIANT.SMALL : PARAGRAPH_VARIANT.MEDIUM} isHighlighted className={`text-neutral-90 ${styles.truncate_after_two_lines}`} data-sentry-element="Paragraph" data-sentry-source-file="ProductListingCard.tsx">
                {brand?.name ? `${brand.name}-` : null}
                {product.name}
              </Paragraph>
            </div>
            {/* {seeDetailsCTA} */}
            <div className="h-[32px]">
              {!isMultipleVariantsInCart && productIntro ? <Paragraph className={`text-xs font-semibold text-neutral-text-light-70 ${styles.truncate_after_two_lines}`}>
                  {productIntro}
                </Paragraph> : null}
            </div>
            <div className="flex flex-row items-center gap-1 mt-1 h-[24px]">
              {!isMultipleVariantsInCart && overallRating ? <>
                  <div className="flex flex-row justify-center items-center gap-1 bg-[#FFF3C2] p-1 rounded w-fit">
                    <Star width={12} height={12} strokeColor={'#8C6500'} />
                    <p className="font-medium text-[#8C6500] text-xs">
                      {overallRating}
                    </p>
                  </div>
                  {totalRatings ? <p className="font-normal text-neutral-text-light-70 text-xs">
                      {totalRatings} ratings
                    </p> : null}
                </> : null}
            </div>
          </>
          <div className="flex-1 min-h-0" aria-hidden />
          {/* {isOutOfStock ? null : (
            <>
              {availableStock && availableStock[activeVariants[0]?.id] ? (
                <div className="bg-error-10 mt-3 p-2 rounded-md text-error-100 text-sm text-center">
                  {availableStock[activeVariants[0].id] === 1
                    ? t('only_one_item_left')
                    : t('only_few_items_left', {
                        availableQty: availableStock[activeVariants[0].id],
                      })}
                </div>
              ) : (
                <VariantAddedMessage
                  activeVariants={activeVariants.map((v) => v.id)}
                />
              )}
            </>
           )} */}
          <div className="flex flex-col gap-2 bg-white rounded-b-xl w-full shrink-0" onClick={stopPropagation}>
            <div className={`flex items-center mb-1 ${!isOutOfStock && 'min-h-[24px]'}`}>
              {!isMultipleVariantsInCart && !isOutOfStock ? <>
                  <ProductPrice variant={displayVariant} showDiscountPercentage />
                  {showEditPrice && <button className="flex items-center ml-2 text-primary-100 cursor-pointer" onClick={onEditClick}>
                      <EditIcon2 className="fill-white stroke-primary-100 mr-1 w-3 h-3" />
                      {t('edit_price')}
                    </button>}
                </> : null}
            </div>
            {isMultipleVariantsInCart ? <div className={`${!isOutOfStock && 'h-[36px]'} flex items-end w-full`}>
                <div className={`flex flex-nowrap items-center gap-1.5 w-full overflow-x-auto overflow-y-hidden ${scrollStyles.scroll}`}>
                  {variantsInCart.map(variant => <button key={variant.id} type="button" className="inline-flex shrink-0 items-center gap-1.5 h-7 px-2 border border-primary-100 rounded-full bg-white" onClick={toggleBottomDrawer}>
                      <span className="text-[11px] font-medium text-primary-100 whitespace-nowrap leading-none">
                        {variant.attribute_value[0].name}
                      </span>
                      <span className="shrink-0 bg-primary-100 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] leading-[18px] text-center">
                        {cartItems[variant.id]}
                      </span>
                    </button>)}
                </div>
              </div> : <VariantLabel variantLabel={displayVariant?.attribute_value[0].name || ''} variantLength={activeVariants.length} onClick={toggleBottomDrawer} isOutOfStock={isOutOfStock} />}

            <HyperLocalAddToCart productVariant={displayVariant} listType={listType} index={position} isOutOfStock={isOutOfStock} length={activeVariants.length} onClick={toggleBottomDrawer} quantity={quantity} componentName={componentName} componentId={componentId} isWishlist={isWishlist} product={product} onShowSimilar={onShowSimilar} btnContainerStyle={isMultipleVariantsInCart ? 'bg-primary-20 border-primary-110 !mt-2' : undefined} data-sentry-element="HyperLocalAddToCart" data-sentry-source-file="ProductListingCard.tsx" />
          </div>
        </section>
      </Link>
      {activeVariants.length > 1 ? <BottomDrawer show={showVariantDrawer} onClose={() => setShowVariantDrawer(false)} ref={bottomDrawerRef} drawerStyle={'md:max-w-xl'} containScroll>
          <section className="flex flex-col min-h-0 max-h-[90vh] bg-gray-10 shadow-[0_-4px_4px_rgba(0,0,0,0.07)] px-4 pt-4 pb-6 rounded-t-xl">
            <ProductVariantList productVariants={activeVariants} listType={listType} position={position} onContine={() => setShowVariantDrawer(false)} componentName={componentName} componentId={componentId} isWishlist={isWishlist} product={product} />
          </section>
        </BottomDrawer> : null}
      <BottomDrawer show={showModifyPriceDrawer} onClose={() => {
      setShowModifyPriceDrawer(false);
    }} data-sentry-element="BottomDrawer" data-sentry-source-file="ProductListingCard.tsx">
        {activeVariants?.[0] ? <ModifyPriceDrawer key={activeVariants[0]?.id} variantList={activeVariants} imageUrl={image_url} name={name} variant={activeVariants[0]} onSubmit={handleModifyPrice} dcId={dcId} /> : null}
      </BottomDrawer>
    </>;
};
export default ProductListingCard;